import React, { useEffect, useState } from 'react';
import { Collapse, Grid, Tooltip, styled } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Colors, Icon, InlineAlert, Text, UnderlinedTextInput } from 'library';
import { BusinessData } from '../business/BusinessModels';
import ProgramTypes from 'parent-portal/BusinessLocation/ProgramTypes';
import { consoleLogInDev } from 'shared';
import { getProgramTypes, IProgramType } from 'api/ProgramTypeApi';
import LocationTypes from 'parent-portal/forms/components/pages/BusinessLocationPage/components/LocationTypes';
import FormSection from './FormSection';

interface BusinessDetailsSectionProps {
    firstName?: string;
    isValidProgramId: boolean;
    state?: string;
    onBusinessDataChange: (data: BusinessData) => void;
    onBusinessErrorChange: (hasError: boolean) => void;
    onNotBusinessClick: () => void;
    expanded: boolean;
    complete: boolean;
    onToggle: () => void;
    onNext: () => void;
}

function getProgramLabel(state?: string) {
    if (state === 'Florida') return 'License Number';
    return 'Provider ID/Program ID/License Number**';
}

const NOT_A_BUSINESS_PATTERNS = ['n/a', 'na', 'none', 'no', '-', 'myself'];

function looksLikePersonName(value: string, firstName?: string) {
    const lower = value.toLowerCase().trim();
    if (NOT_A_BUSINESS_PATTERNS.includes(lower)) return true;
    if (firstName && lower === firstName.toLowerCase().trim()) return true;
    return false;
}

export default function BusinessDetailsSection({
    firstName,
    isValidProgramId,
    state,
    onBusinessDataChange,
    onBusinessErrorChange,
    onNotBusinessClick,
    expanded,
    complete,
    onToggle,
    onNext,
}: BusinessDetailsSectionProps) {
    const [name, setName] = useState<string>();
    const [locationTypes, setLocationTypes] = useState<number[]>([]);
    const [programId, setProgramId] = useState<string>();
    const [nameError, setNameError] = useState(false);
    const [showNotBusinessHint, setShowNotBusinessHint] = useState(false);
    const [programTypeOptions, setProgramTypeOptions] = useState<IProgramType[]>([]);
    const [programTypes, setProgramTypes] = useState<string[]>([]);

    const programLabel = getProgramLabel(state);

    useEffect(() => {
        setNameError(name === '');

        if (!!name && !nameError) {
            const data: BusinessData = {
                name,
                programId,
                programTypes: programTypeOptions.filter((x) => programTypes.includes(x.name)).map((x) => x.id),
                locationTypes,
            };
            onBusinessDataChange(data);
            onBusinessErrorChange(false);
        } else {
            onBusinessErrorChange(true);
        }
    }, [name, nameError, programId, programTypes, locationTypes]);

    useEffect(() => {
        if (!state) {
            if (programTypes.length) setProgramTypes([]);
        } else {
            getProgramTypes(state)
                .then((response) => {
                    setProgramTypeOptions(response);
                    if (!response.length) setProgramTypes([]);
                })
                .catch(consoleLogInDev);
        }
    }, [state]);

    function handleNameChange(value: string) {
        setName(value);
        setShowNotBusinessHint(looksLikePersonName(value, firstName));
    }

    function onProgramTypesChanged(option: string) {
        if (programTypes.includes(option)) {
            setProgramTypes(programTypes.filter((x) => x !== option));
        } else {
            setProgramTypes([...programTypes, option]);
        }
    }

    return (
        <FormSection
            title="Business Details"
            icon={<BusinessIcon />}
            expanded={expanded}
            complete={complete}
            onToggle={onToggle}
            onNext={onNext}
            autoFocusId="txtBusinessName"
        >
            <Grid item xs={12}>
                <UnderlinedTextInput
                    id="txtBusinessName"
                    label="Business Name*"
                    value={name ?? ''}
                    onChange={handleNameChange}
                    error={nameError}
                    errorText="Please enter a business name."
                />
                <Collapse in={showNotBusinessHint} unmountOnExit>
                    <InlineAlert
                        severity="info"
                        style={{ marginTop: 8 }}
                        action={
                            <NotBusinessLink role="button" tabIndex={0} onClick={onNotBusinessClick}>
                                <Text variant="caption" bold color={Colors.darkNavy}>
                                    Switch to Teacher
                                </Text>
                            </NotBusinessLink>
                        }
                    >
                        <Text variant="caption">
                            This doesn&apos;t look like a business name. If you&apos;re a teacher looking for work,
                            select the Teacher card above instead.
                        </Text>
                    </InlineAlert>
                </Collapse>
            </Grid>

            <Grid item xs={12}>
                <LocationTypes selectedTypes={locationTypes} setSelectedTypes={setLocationTypes} />
            </Grid>

            <Grid item xs={12}>
                <ProgramIdRow>
                    <Tooltip
                        placement="top"
                        title="Enter the Program Number associated with the address of the location you entered above."
                    >
                        <Icon
                            size={24}
                            icon={<HelpOutlineIcon sx={{ fontSize: 16, color: 'inherit' }} />}
                            backgroundColor={Colors.lightTurq}
                            color={Colors.turquoise}
                            style={{ marginRight: 10, marginBottom: 6, cursor: 'help' }}
                        />
                    </Tooltip>
                    <UnderlinedTextInput
                        id="txtProgramId"
                        label={programLabel}
                        value={programId ?? ''}
                        onChange={(value: string) => setProgramId(value)}
                        error={!isValidProgramId}
                        errorText="Please enter a valid program ID."
                    />
                </ProgramIdRow>
            </Grid>

            {programTypeOptions.length > 0 && (
                <Grid item xs={12}>
                    <Text textStyle={{ marginBottom: 8 }}>
                        Do you currently participate in any of the following state programs?
                    </Text>
                    <ProgramTypes
                        options={programTypeOptions.map((x) => x.name)}
                        programTypes={programTypes}
                        onProgramTypesChanged={onProgramTypesChanged}
                    />
                </Grid>
            )}
        </FormSection>
    );
}

const ProgramIdRow = styled('div')({
    display: 'flex',
    alignItems: 'flex-end',
    width: '100%',
});

const NotBusinessLink = styled('span')({
    background: 'none',
    border: `1px solid ${Colors.darkNavy}`,
    borderRadius: 16,
    padding: '4px 12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
        backgroundColor: Colors.fill,
    },
});
